<template>
  <div
    aria-atomic="true"
    aria-live="polite"
  >
    <div
      v-if="visible"
      class="is-dark-surface tw:fixed tw:top-0 tw:right-0 tw:left-0 tw:z-[10000] tw:flex tw:w-full tw:items-center tw:justify-center tw:bg-blue-700 tw:py-1 tw:text-center tw:font-bold tw:text-white"
    >
      <button
        class="tw:flex tw:cursor-pointer tw:items-center tw:gap-1 tw:border-0 tw:bg-transparent tw:font-[inherit] tw:text-[inherit] tw:font-bold tw:text-inherit"
        data-testid="upgrade-bar"
        type="button"
        @click="reload"
      >
        <BaseIcon icon="info" />
        <span>
          {{
            getLocaleText("upgrade_bar.new_version_of_platform", {
              platformName: appConfig.platformName,
            })
          }}
        </span>
      </button>
      <BaseHasPermission permission="has_advanced_access">
        <BaseButton
          :aria-label="getLocaleText('global.close')"
          button-class="upgrade-bar__close tw:absolute tw:right-2"
          data-testid="upgrade-bar-close"
          dense
          flat
          icon="close"
          new-design
          @click="dismiss"
        />
      </BaseHasPermission>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { onBeforeUnmount, onMounted, ref } from "vue";

import { track } from "shared/boot/analytics";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseHasPermission } from "shared/components/base";
import BaseIcon from "shared/components/base/BaseIcon";
import { MILLISECONDS } from "shared/constants";
import StorageService from "shared/services/StorageService";
import { useGlobalStore } from "shared/stores/global";

interface OnlineVersion {
  hash?: string;
  version: string;
}

const FIVE_MINUTES = 5 * MILLISECONDS.minute;
const EIGHTEEN_HOURS = 18 * MILLISECONDS.hour;

const loadedVersion: OnlineVersion = {
  hash: "__commit_hash__",
  version: "__version_number__",
};

const { appConfig } = storeToRefs(useGlobalStore());

const visible = ref(false);
const showAgainAt = ref(0);

let interval: ReturnType<typeof setInterval> | null = null;

function shouldShowBar(onlineVersion: OnlineVersion): boolean {
  return (
    onlineVersion.version !== loadedVersion.version ||
    (loadedVersion.hash !== onlineVersion.hash &&
      showAgainAt.value >= Date.now())
  );
}

async function checkForNewVersion(): Promise<void> {
  try {
    const { data: onlineVersion } = await $streemApiV1.get<OnlineVersion>(
      "version.json",
      { baseURL: window.location.origin }
    );

    if (shouldShowBar(onlineVersion)) {
      visible.value = true;
      StorageService.set("lastUpdate", Date.now(), sessionStorage);
    }
  } catch {
    // do nothing
  }
}

function reload(): void {
  track("Upgraded App", { version: loadedVersion });
  window.location.reload();
}

function stopPolling(): void {
  if (!interval) return;

  clearInterval(interval);
  interval = null;
}

function dismiss(): void {
  visible.value = false;
  stopPolling();
}

onMounted(async () => {
  if (import.meta.env.VITE_ENABLE_UPGRADE_BAR) {
    interval = setInterval(async () => {
      if (!visible.value) {
        await checkForNewVersion();
      }
    }, FIVE_MINUTES);

    const last = await StorageService.get("lastUpdate");
    showAgainAt.value = (last ? Number(last) : 0) + EIGHTEEN_HOURS;
  }
});

onBeforeUnmount(stopPolling);

defineExpose({ checkForNewVersion });
</script>

<style lang="scss" scoped>
.base-button.upgrade-bar__close {
  --c-button-color: var(--s-color-font-light);
  --c-button-color-hover: var(--s-color-font-light);
  --c-button-hover: color-mix(
    in srgb,
    var(--s-color-font-light) 20%,
    transparent
  );

  &:focus-visible {
    outline-color: var(--s-color-font-light);
  }
}
</style>
